'use client';

import { useCallback, useRef } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────

const DEFAULT_SCROLL_AMOUNT = 200;
const SCROLL_END_THRESHOLD = 50;

// ─── Component ────────────────────────────────────────────────────────────

interface ScrollableRowProps {
  children: React.ReactNode;
  /** Show navigation arrows when true. */
  showArrows?: boolean;
  /** Label displayed above the scrollable area with inline arrows. */
  label?: string;
  /** Pixels to scroll per arrow click. @default 200 */
  scrollAmount?: number;
  className?: string;
  /** Compact mode for chat-bubble embeds — tighter gaps, smaller label
   *  and arrow icons, light-only text color. */
  compact?: boolean;
  /** Called when the user scrolls near the end of the row. */
  onScrollEnd?: () => void;
}

export function ScrollableRow({
  children,
  showArrows = false,
  label,
  scrollAmount = DEFAULT_SCROLL_AMOUNT,
  className,
  compact = false,
  onScrollEnd,
}: ScrollableRowProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const onScrollEndRef = useRef(onScrollEnd);
  onScrollEndRef.current = onScrollEnd;

  const checkScrollEnd = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !onScrollEndRef.current) return;
    const nearEnd =
      el.scrollLeft + el.clientWidth >= el.scrollWidth - SCROLL_END_THRESHOLD;
    if (nearEnd) onScrollEndRef.current();
  }, []);

  const handleScrollLeft = useCallback(() => {
    scrollContainerRef.current?.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });
  }, [scrollAmount]);

  const handleScrollRight = useCallback(() => {
    scrollContainerRef.current?.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

    // Check after smooth scroll animation completes
    setTimeout(checkScrollEnd, 350);
  }, [scrollAmount, checkScrollEnd]);

  const labelClass = compact
    ? 'force-light:text-gray-400 text-[8px] font-normal tracking-wide text-gray-400 dark:text-gray-500'
    : 'text-xs font-medium text-gray-900 dark:text-white';
  const arrowsContainerClass = compact
    ? 'flex items-center gap-2'
    : 'flex items-center gap-4';
  const arrowIconClass = compact
    ? 'h-3 w-3 force-light:text-gray-500 text-gray-500 dark:text-gray-400'
    : 'h-4 w-4 text-gray-500 dark:text-gray-400';
  const wrapperClass = compact ? 'flex flex-col gap-1' : 'flex flex-col gap-3';
  const defaultScrollContainerClass = compact
    ? 'flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
    : 'flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

  return (
    <div className={wrapperClass}>
      {(label || showArrows) && (
        <div className="flex items-center justify-between">
          {label && <p className={labelClass}>{label}</p>}
          {showArrows && (
            <div className={arrowsContainerClass}>
              <button
                type="button"
                onClick={handleScrollLeft}
                aria-label="Scroll left"
              >
                <ChevronLeft className={arrowIconClass} />
              </button>
              <button
                type="button"
                onClick={handleScrollRight}
                aria-label="Scroll right"
              >
                <ChevronRight className={arrowIconClass} />
              </button>
            </div>
          )}
        </div>
      )}
      <div
        ref={scrollContainerRef}
        onScroll={checkScrollEnd}
        className={className ?? defaultScrollContainerClass}
      >
        {children}
      </div>
    </div>
  );
}
